export enum ProfileViewVisibility {
  NONE = 'none',           // Can't see profile views
  ANONYMOUS = 'anonymous', // "Someone from USA viewed your profile"
  FULL = 'full'           // Complete viewer information
}

export interface PlanFeatures {
  aiMessagesPerDay: number;
  knowledgeBaseFileAmount: number;
  matchesPerDay: number;
  webLinksPerResponse: number;
  privateGroupParticipants: number | 'unlimited';
  fileUploads: boolean;
  peopleInteractions: boolean;
  fullNameViewing?: boolean;
  nameObfuscation?: boolean;
  chatViewsTracking?: boolean;
  tokensPerMonth?: number;
  customDatabase?: boolean;
  organizationFeed?: boolean;
  priorityBandwidth?: boolean;
  expandedContext?: boolean;
  ssoAndDomainControl?: boolean;
  auditLogs?: boolean;
  customIntegrations?: boolean;
  roleBasedAccess?: boolean;
  prioritySupport?: boolean;
  teamManagement?: boolean;
  profileViewVisibility: ProfileViewVisibility
  invisibleMode?: boolean
  canDisablePeopleRecommendations?: boolean;
  canDisableLinkAddOns?: boolean;
  organizationMembershipDays?: number | 'unlimited';
  maxUsers?: number;
  maxUsersAllowedInOrg?: number; // Maximum users allowed in the organization
}

export interface PricingCategory {
  name: string;
  plans: Record<string, PlanFeatures>;
}

export const pricingPlans: PricingCategory[] = [
  {
    name: 'personal',
    plans: {
      /**
       * Free plan with unlimited access to all features.
       * We've removed all limitations since we now offer a 14-day trial period
       * for organizations (see hasTrialExpired function).
       * This allows users to experience the full platform capabilities
       * before deciding on a paid subscription.
       * !NOTE: we only offer mixus_business and mixus_enterprise now.
       */
      free: {
        aiMessagesPerDay: -1,
        knowledgeBaseFileAmount: 100,
        matchesPerDay: -1,
        webLinksPerResponse: -1,
        privateGroupParticipants: 'unlimited',
        fileUploads: true,
        peopleInteractions: true,
        fullNameViewing: true,
        nameObfuscation: true,
        chatViewsTracking: true,
        tokensPerMonth: -1,
        customDatabase: false,
        organizationFeed: true,
        priorityBandwidth: false,
        expandedContext: true,
        ssoAndDomainControl: true,
        auditLogs: true,
        customIntegrations: true,
        roleBasedAccess: true,
        prioritySupport: true,
        teamManagement: true,
        profileViewVisibility: ProfileViewVisibility.FULL,
        invisibleMode: true,
        canDisablePeopleRecommendations: true,
        canDisableLinkAddOns: true,
        organizationMembershipDays: 'unlimited',
        maxUsers: -1,
        maxUsersAllowedInOrg: -1,
      },
      premium: {
        aiMessagesPerDay: 250,
        knowledgeBaseFileAmount: 100,
        matchesPerDay: 200,
        webLinksPerResponse: 5,
        privateGroupParticipants: 'unlimited',
        fileUploads: true,
        peopleInteractions: true,
        fullNameViewing: true,
        nameObfuscation: true,
        chatViewsTracking: true,
        profileViewVisibility: ProfileViewVisibility.FULL,
        invisibleMode: true,
        canDisablePeopleRecommendations: true,
        canDisableLinkAddOns: true,
        organizationMembershipDays: 'unlimited',
      },
    },
  },
  {
    name: 'business',
    plans: {
      team_10: {
        aiMessagesPerDay: 500,
        knowledgeBaseFileAmount: 100,
        matchesPerDay: 250,
        webLinksPerResponse: 10,
        privateGroupParticipants: 'unlimited',
        fileUploads: true,
        peopleInteractions: true,
        fullNameViewing: true,
        nameObfuscation: true,
        chatViewsTracking: true,
        tokensPerMonth: 25000000,
        roleBasedAccess: true,
        prioritySupport: true,
        teamManagement: true,
        profileViewVisibility: ProfileViewVisibility.FULL,
        canDisablePeopleRecommendations: true,
        canDisableLinkAddOns: true,
        organizationMembershipDays: 'unlimited',
        maxUsers: 10,
        invisibleMode: true,
        maxUsersAllowedInOrg: 0, // Will be dynamically set based on purchased seats
      },
      team_50: {
        aiMessagesPerDay: 500,
        knowledgeBaseFileAmount: 100,
        matchesPerDay: 250,
        webLinksPerResponse: 10,
        privateGroupParticipants: 'unlimited',
        fileUploads: true,
        peopleInteractions: true,
        fullNameViewing: true,
        nameObfuscation: true,
        chatViewsTracking: true,
        tokensPerMonth: 25000000,
        roleBasedAccess: true,
        prioritySupport: true,
        teamManagement: true,
        profileViewVisibility: ProfileViewVisibility.FULL,
        canDisablePeopleRecommendations: true,
        canDisableLinkAddOns: true,
        organizationMembershipDays: 'unlimited',
        maxUsers: 50,
        invisibleMode: true,
        maxUsersAllowedInOrg: 0, // Will be dynamically set based on purchased seats
      },
      team_100: {
        aiMessagesPerDay: 500,
        knowledgeBaseFileAmount: 100,
        matchesPerDay: 250,
        webLinksPerResponse: 10,
        privateGroupParticipants: 'unlimited',
        fileUploads: true,
        peopleInteractions: true,
        fullNameViewing: true,
        nameObfuscation: true,
        chatViewsTracking: true,
        tokensPerMonth: 25000000,
        roleBasedAccess: true,
        prioritySupport: true,
        teamManagement: true,
        profileViewVisibility: ProfileViewVisibility.FULL,
        canDisablePeopleRecommendations: true,
        canDisableLinkAddOns: true,
        organizationMembershipDays: 'unlimited',
        maxUsers: 100,
        maxUsersAllowedInOrg: 0, // Will be dynamically set based on purchased seats
      },
      enterprise: {
        aiMessagesPerDay: 500,
        knowledgeBaseFileAmount: 100,
        matchesPerDay: -1,
        webLinksPerResponse: 5,
        privateGroupParticipants: 'unlimited',
        fileUploads: true,
        peopleInteractions: true,
        fullNameViewing: true,
        nameObfuscation: true,
        chatViewsTracking: true,
        tokensPerMonth: 25000000,
        customDatabase: true,
        organizationFeed: true,
        priorityBandwidth: true,
        expandedContext: true,
        ssoAndDomainControl: true,
        auditLogs: true,
        customIntegrations: true,
        roleBasedAccess: true,
        prioritySupport: true,
        teamManagement: true,
        profileViewVisibility: ProfileViewVisibility.FULL,
        canDisablePeopleRecommendations: true,
        canDisableLinkAddOns: true,
        organizationMembershipDays: 'unlimited',
        maxUsers: 1000,
        maxUsersAllowedInOrg: 0, // Will be dynamically set based on purchased seats
      },
      //! NEW SUBSCRIPTION OPTIONS: (only these 2 available now)
      // MIXUS BUSINESS OFFERING (number of seats can be set exactly)
      mixus_business: {
        aiMessagesPerDay: -1, // Unlimited
        knowledgeBaseFileAmount: 100,
        matchesPerDay: -1, // Unlimited
        webLinksPerResponse: -1, // Unlimited
        privateGroupParticipants: 'unlimited',
        fileUploads: true,
        peopleInteractions: true,
        fullNameViewing: true,
        nameObfuscation: true,
        chatViewsTracking: true,
        tokensPerMonth: -1, // Unlimited
        customDatabase: false,
        organizationFeed: true,
        priorityBandwidth: true,
        expandedContext: true,
        roleBasedAccess: true,
        prioritySupport: true,
        teamManagement: true,
        profileViewVisibility: ProfileViewVisibility.FULL,
        canDisablePeopleRecommendations: true,
        canDisableLinkAddOns: true,
        organizationMembershipDays: 'unlimited',
        maxUsers: -1, // Unlimited
        invisibleMode: true,
        maxUsersAllowedInOrg: 0, // Will be dynamically set based on purchased seats
      },
      // SELF-HOSTED ENTERPRISE OFFERING (contact sales to purchase)
      mixus_enterprise: {
        aiMessagesPerDay: -1, // Unlimited
        knowledgeBaseFileAmount: -1, // Unlimited
        matchesPerDay: -1, // Unlimited
        webLinksPerResponse: -1, // Unlimited
        privateGroupParticipants: 'unlimited',
        fileUploads: true,
        peopleInteractions: true,
        fullNameViewing: true,
        nameObfuscation: true,
        chatViewsTracking: true,
        tokensPerMonth: -1, // Unlimited
        customDatabase: true,
        organizationFeed: true,
        priorityBandwidth: true,
        expandedContext: true,
        ssoAndDomainControl: true,
        auditLogs: true,
        customIntegrations: true,
        roleBasedAccess: true,
        prioritySupport: true,
        teamManagement: true,
        profileViewVisibility: ProfileViewVisibility.FULL,
        canDisablePeopleRecommendations: true,
        canDisableLinkAddOns: true,
        organizationMembershipDays: 'unlimited',
        maxUsers: -1, // Unlimited
        invisibleMode: true,
        maxUsersAllowedInOrg: 0, // Will be dynamically set based on purchased seats
      },
    },
  },
];

// Add mapping for product IDs to plan categories and names
export const PRODUCT_ID_TO_PLAN = {
  [process.env.NEXT_PUBLIC_STRIPE_PREMIUM_PRODUCT_ID!]: { category: 'personal', name: 'premium', displayName: 'premium' },
  [process.env.NEXT_PUBLIC_STRIPE_TEAM10_PRODUCT_ID!]: { category: 'business', name: 'team_10', displayName: 'team' },
  [process.env.NEXT_PUBLIC_STRIPE_TEAM50_PRODUCT_ID!]: { category: 'business', name: 'team_50', displayName: 'team' },
  [process.env.NEXT_PUBLIC_STRIPE_TEAM100_PRODUCT_ID!]: { category: 'business', name: 'team_100', displayName: 'team' },
  [process.env.NEXT_PUBLIC_STRIPE_MIXUS_BUSINESS_PRODUCT_ID!]: { category: 'business', name: 'mixus_business', displayName: 'business' },
  [process.env.NEXT_PUBLIC_STRIPE_MIXUS_ENTERPRISE_PRODUCT_ID!]: { category: 'business', name: 'mixus_enterprise', displayName: 'enterprise' },
} as const;

// Helper functions
export function getPlanFeatures(category: string, planName: string): PlanFeatures | undefined {
  const categoryData = pricingPlans.find(cat => cat.name === category);
  return categoryData?.plans[planName];
}

export function isFeatureAvailable(category: string, planName: string, feature: keyof PlanFeatures): boolean {
  const plan = getPlanFeatures(category, planName);
  return !!plan?.[feature];
}

export function getFeatureValue<T extends keyof PlanFeatures>(
  subscriptionDetails: { subscription_id?: string; product_id?: string; is_active?: boolean; } | undefined,
  feature: T
): PlanFeatures[T] | undefined {
  const { category, name } = getPlanFromSubscription(subscriptionDetails);
  const plan = getPlanFeatures(category, name);
  return plan?.[feature];
}

export function getPlanFromSubscription(subscriptionDetails?: {
  subscription_id?: string;
  product_id?: string;
  is_active?: boolean;
}): { category: string; name: string } {
  // If no subscription or inactive, return free plan
  if (!subscriptionDetails?.is_active) {
    return { category: 'personal', name: 'free' };
  }

  // Get plan from product ID mapping
  const plan = PRODUCT_ID_TO_PLAN[subscriptionDetails.product_id as keyof typeof PRODUCT_ID_TO_PLAN];

  // If product ID not found in mapping, default to free plan
  return plan || { category: 'personal', name: 'free' };
}

export function hasTrialExpired(orgCreatedAt?: Date | number): boolean {
  if (!orgCreatedAt) return true;
  
  const trialDays = 14; // 14-day trial period
  
  // Convert the orgCreatedAt to a Date object if it's a timestamp
  const createdAtDate = typeof orgCreatedAt === 'number' 
    ? new Date(orgCreatedAt) 
    : orgCreatedAt;
  
  const trialEndDate = new Date(createdAtDate);
  trialEndDate.setDate(trialEndDate.getDate() + trialDays);
  
  return new Date() > trialEndDate;
}

export function shouldShowSubscriptionDialog(params: {
  organization: {
    created_at: Date;
    subscription_details?: {
      subscription_id: string | null;
      product_id: string;
      is_active: boolean;
      createdAt: Date;
      maxUsersAllowedInOrg?: number;
    } | null;
  } | null;
}): boolean {
  const { organization } = params

  // If no organization, don't show dialog
  if (!organization) {
    return false
  }

  // If subscription is active, don't show dialog
  if (organization.subscription_details?.is_active) {
    return false
  }

  // Check if organization is within trial period
  const isTrialExpired = hasTrialExpired(organization.created_at)
  if (!isTrialExpired) {
    return false // Don't show dialog during trial period
  }

  return true // Show dialog if trial expired and no active subscription
}
